import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { workflowsApi } from '../services/api';
import { ArrowLeft, CheckCircle, XCircle, Loader, Clock } from 'lucide-react';

interface ExecutionSummary {
  id: number;
  status: string;
  mode: string;
  startedAt: string | null;
  stoppedAt: string | null;
  durationMs: number | null;
}

interface ExecutionsData {
  workflow: { id: string; name: string };
  executions: ExecutionSummary[];
  total: number;
  page: number;
  limit: number;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'success') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
        <CheckCircle size={12} className="mr-1" /> Success
      </span>
    );
  }
  if (status === 'error' || status === 'crashed') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
        <XCircle size={12} className="mr-1" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
      <Loader size={12} className="mr-1" /> {status}
    </span>
  );
}

export default function WorkflowExecutionsPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ExecutionsData | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    workflowsApi
      .getExecutions(id, page)
      .then((res) => setData(res.data.data))
      .catch(() => setError('Failed to load executions'))
      .finally(() => setLoading(false));
  }, [id, page]);

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div>
      <Link
        to="/workflows"
        className="inline-flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
      >
        <ArrowLeft size={18} className="mr-2" /> Back to workflows
      </Link>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">
          {data?.workflow.name || 'Workflow'}
        </h2>
        <p className="text-gray-400">
          {data ? `${data.total} execution${data.total === 1 ? '' : 's'} in the last 30 days` : ''}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-orange border-t-transparent" />
        </div>
      ) : data && data.executions.length === 0 ? (
        <div className="card text-center py-12">
          <Clock className="mx-auto text-gray-600 mb-4" size={40} />
          <p className="text-gray-400">No executions in the last 30 days.</p>
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 text-sm">
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Started</th>
                  <th className="px-6 py-3 font-medium">Finished</th>
                  <th className="px-6 py-3 font-medium">Duration</th>
                  <th className="px-6 py-3 font-medium">Trigger</th>
                </tr>
              </thead>
              <tbody>
                {data?.executions.map((ex) => (
                  <tr key={ex.id} className="border-b border-gray-700/50 text-sm">
                    <td className="px-6 py-3">
                      <StatusBadge status={ex.status} />
                    </td>
                    <td className="px-6 py-3 text-gray-300">{formatDateTime(ex.startedAt)}</td>
                    <td className="px-6 py-3 text-gray-300">{formatDateTime(ex.stoppedAt)}</td>
                    <td className="px-6 py-3 text-gray-300">{formatDuration(ex.durationMs)}</td>
                    <td className="px-6 py-3 text-gray-400 capitalize">{ex.mode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                className="btn btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="text-gray-400 text-sm">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
